import React from "react";
import styled, { keyframes } from "styled-components";
import { Github, Linkedin, ChevronDown } from "lucide-react";
import ParticleBackground from "../Components/ParticleBackground";
import { PageTransition } from "../Components/AnimatedComponents";

const HomePage: React.FC = () => {
  return (
    <PageTransition>
      <HomePageStyled>
        <ParticleBackground />
        <div className="hero-content">
          <p className="greeting">Hey there, I'm</p>
          <h1 className="name">
            Saif Al-Din <span>Ali</span>
          </h1>
          <p className="role">Software Engineer</p>
          <p className="description">
            Building performant, scalable and elegant software solutions.
            <br />
            Passionate about full-stack development, machine learning & open
            source.
          </p>
          <div className="social-links">
            <a
              href="https://github.com/saifaldin14"
              target="_blank"
              rel="noreferrer"
              className="social-btn"
              title="GitHub"
            >
              <Github size={20} />
            </a>
            <a
              href="https://www.linkedin.com/in/saif-al-din-ali-78b061182/"
              target="_blank"
              rel="noreferrer"
              className="social-btn"
              title="LinkedIn"
            >
              <Linkedin size={20} />
            </a>
          </div>
        </div>
        <div className="scroll-indicator">
          <ChevronDown size={20} />
        </div>
      </HomePageStyled>
    </PageTransition>
  );
};

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(6px); }
`;

const HomePageStyled = styled.header`
  width: 100%;
  height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  .hero-content {
    position: relative;
    z-index: 1;
    text-align: center;
    max-width: 700px;
    padding: 0 2rem;

    .greeting {
      font-size: 1rem;
      font-weight: 500;
      color: var(--primary-color);
      text-transform: uppercase;
      letter-spacing: 0.15em;
      margin-bottom: 0.75rem;
      animation: ${fadeInUp} 0.6s ease both;
    }

    .name {
      font-size: 4.5rem;
      font-weight: 900;
      line-height: 1.05;
      letter-spacing: -0.03em;
      margin-bottom: 1rem;
      animation: ${fadeInUp} 0.6s ease 0.1s both;

      span {
        background: linear-gradient(
          135deg,
          var(--primary-color),
          var(--primary-color-light),
          #a78bfa
        );
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }

      @media screen and (max-width: 768px) {
        font-size: 3rem;
      }

      @media screen and (max-width: 480px) {
        font-size: 2.25rem;
      }
    }

    .role {
      font-size: 1.2rem;
      font-weight: 500;
      color: var(--white-color);
      margin-bottom: 1rem;
      animation: ${fadeInUp} 0.6s ease 0.2s both;

      @media screen and (max-width: 480px) {
        font-size: 1rem;
      }
    }

    .description {
      font-size: 0.95rem;
      color: var(--font-light-color);
      line-height: 1.7;
      margin-bottom: 2rem;
      animation: ${fadeInUp} 0.6s ease 0.3s both;
    }

    .social-links {
      display: flex;
      justify-content: center;
      gap: 0.75rem;
      animation: ${fadeInUp} 0.6s ease 0.4s both;

      .social-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2.75rem;
        height: 2.75rem;
        border-radius: 12px;
        border: 1px solid var(--border-color);
        color: var(--font-light-color);
        transition: all 0.25s ease;
        background: var(--card-bg);
        backdrop-filter: blur(8px);

        &:hover {
          border-color: var(--primary-color);
          color: var(--primary-color);
          transform: translateY(-3px);
          box-shadow: var(--shadow-md);
        }
      }
    }
  }

  .scroll-indicator {
    position: absolute;
    bottom: 2rem;
    left: 50%;
    transform: translateX(-50%);
    color: var(--font-light-color);
    animation: ${bounce} 2s ease-in-out infinite;
    opacity: 0.5;
    z-index: 1;
  }
`;

export default HomePage;
